/* ==========================================================================
   1-custom-track.js — shared attribution + analytics bridge.

   Loaded (deferred) by BOTH 1-custom-adv1.liquid and 1-custom-pdp.liquid,
   before each page's own script, so a visitor keeps one identity and one
   attribution record across the advertorial -> PDP hop.

   ── THE JOIN KEY ──────────────────────────────────────────────────────────
   Everything downstream depends on ONE id existing in all three systems:
       PostHog   distinct_id   (+ regalia_id super property)
       Meta      external_id   (+ eventID, for CAPI dedup)
       Klaviyo   posthog_distinct_id / regalia_id profile properties

   regalia_id is our own UUID in localStorage. It is used rather than
   posthog.get_distinct_id() because it exists SYNCHRONOUSLY from first paint -
   the PostHog loader stub returns undefined until array.js lands, so anything
   firing early would otherwise get an empty id and break the join silently.

   ── WHY THERE IS NO trackCustom() HELPER ──────────────────────────────────
   Shopify's native CAPI (facebookCapiEnabled) sends server-side copies of ITS
   OWN standard events only - page_viewed, add_to_cart, checkout_started,
   purchase. There is no hook, setting or supported route by which an event name
   minted in theme JS gets a server-side copy. So a custom event is browser-only
   forever: exposed to ad blockers, ITP and iOS opt-outs with no backstop, and
   it will always read low against anything CAPI also covers. It cannot even be
   sanity-checked against the CAPI number, because the gap between the two is
   tracking loss and real user abandonment mixed together with no way to
   separate them.

   An AdvertorialCTAClick event was built on this basis and removed once that
   was understood. Do not add one back.

   The rule that replaced it: IF A NUMBER HAS TO BE RIGHT, CARRY IT ON A URL, so
   it rides Shopify's own page_viewed over both pixel AND CAPI and deduplicates
   on Shopify's shared sh- event id. That is what the ?from=adv1_* markers on
   the advertorial's CTAs are for, and why the advertorial -> PDP click-through
   rate is measured at the ARRIVAL and never at the click.

   ── WHY window.fbq IS REACHABLE AT ALL ────────────────────────────────────
   Most Shopify web pixels run in a sandboxed iframe and are invisible to theme
   JS. The Facebook & Instagram sales-channel app is the exception: its entry in
   webPixelsConfigList carries "runtimeContext":"OPEN", so it runs in the top
   window. (Klaviyo's, right beside it, is STRICT - which is why this file talks
   to Klaviyo over its public /client/ HTTP endpoints instead of window.klaviyo.)

   THAT IS SHOPIFY'S SETTING, NOT OURS. If it ever flips to STRICT, window.fbq
   disappears and every Meta call here goes silent. loadMetaBase() below is the
   escape hatch for exactly that - see the note on CONFIG.metaPixelId.

   ── DO NOT SEND STANDARD EVENTS SHOPIFY ALREADY SENDS ─────────────────────
   Meta deduplicates only on a matching (event_name, eventID) pair, and Shopify
   mints its sh-<uuid> ids inside the pixel sandbox where theme JS cannot read
   them. So a second call is a second COUNTED event, not a deduplicated one.
   That is why the PDP calls fb() for NEITHER AddToCart NOR InitiateCheckout:
   Shopify's own add_to_cart and checkout_started cover both, over pixel and
   CAPI.

   InitiateCheckout used to be ours. It was only ever ours because the drawer's
   checkout button was intercepted to open a waitlist popup, so Shopify never
   saw a checkout and checkout_started never fired. The button is a real link to
   /checkout now, so that exemption is gone - a call here would double-count.
   Do not add one back.

   Lead is the one standard event still worth sending from theme JS, because
   Shopify has no equivalent of it - and it is then the only Meta event of ours
   carrying external_id, since Shopify's native ones match on IP + user agent
   only. Nothing calls it today; the waitlist signup was its only caller.

   ── ON THE KEYS BELOW ─────────────────────────────────────────────────────
   The PostHog project token and the Klaviyo company id are PUBLIC, write-only
   client credentials designed to ship in browser code. No private API key,
   admin token or secret belongs in this file or in any comment in it.
   ========================================================================== */

(function (window, document) {
  'use strict';

  if (window.RegaliaTrack) return;

  /* ── Config ────────────────────────────────────────────────────────────── */
  var CONFIG = {
    /* PostHog project API key (PostHog calls it the "Project API Key"; it
       starts with phc_). Public and write-only by design, so it is safe in
       browser code - but it decides WHICH PostHog project every event from this
       store lands in. Two stores sharing one key produce a single mixed dataset
       that cannot be split apart afterwards.

       This points at project id 600864 on US Cloud, which exists to keep this
       store's dataset apart from the cocoa flavanols project. Both sit on the
       trynativia.com domain and both reach PostHog through the
       j.trynativia.com proxy below, so neither the domain nor the proxy
       separates the two - this token is the only thing that does. It is
       therefore the first value to check if events ever turn up in the wrong
       project.

       WHERE TO GET IT: PostHog -> Settings -> Project -> Project API Key, in
       the project belonging to THIS store. */
    posthogToken: 'phc_pTX85FRSDNT2SCouYsGv772xLFZmjHsKsSyMPtchcubQ',

    /* PostHog ingestion host. Prefer a managed reverse proxy on a subdomain of
       THIS store rather than a posthog.com hostname, because every posthog.com
       hostname sits on the standard ad-blocker lists and a store subdomain does
       not. PostHog operates and terminates that proxy (Settings -> Managed
       reverse proxy); on our side it is a DNS record, so there is nothing here
       to deploy or keep alive.

       This value is also where the loader below gets the ASSET host: it takes
       api_host and replaces ".i.posthog.com" with "-assets.i.posthog.com", so
       the two supported shapes behave differently and both are fine:
         - a custom proxy subdomain (https://ph.example.com): the replace
           matches nothing, and array.js is fetched from
           https://ph.example.com/static/array.js, which the proxy serves.
         - a plain regional host (https://us.i.posthog.com): array.js is fetched
           from https://us-assets.i.posthog.com/static/array.js.
       Both halves hang off this one hostname by design: if it ever stops
       resolving, PostHog goes silent outright instead of half-working, and
       nothing else in this file is affected.

       The proxy is a pass-through to the US ingestion endpoint and does not
       pick a project - the token above does - so one proxy domain serves every
       project on this domain. That is why splitting the cocoa flavanols
       dataset out into its own project needed no change here, and why this
       value stays as it is.

       WHERE TO GET IT: PostHog -> Settings -> Project -> Managed reverse proxy,
       once a proxy exists for this store's own domain. If no proxy is set up,
       use the plain regional ingestion host shown next to the project API key
       (https://us.i.posthog.com or https://eu.i.posthog.com). Include the
       scheme; no trailing slash. */
    posthogHost: 'https://j.trynativia.com',

    /* Where the SDK points links meant for a human - session replay URLs, the
       toolbar. The proxy carries ingestion and assets only, so without this
       those links get built against a host that serves no UI. */
    posthogUiHost: 'https://us.posthog.com',

    posthogDefaults: '2026-05-30',

    /* Klaviyo public company ID - also shown as the Site ID / Public API Key,
       a short 6-character string. Public and write-only; it selects which
       Klaviyo ACCOUNT the /client/ endpoints below write profiles and events
       into. Point it at another store's account and that store receives this
       store's subscribers.

       WHERE TO GET IT: Klaviyo -> Settings -> Account -> API keys -> Public API
       Key (Site ID), in THIS store's own Klaviyo account. */
    klaviyoCompanyId: 'REPLACE_ME_KLAVIYO_COMPANY_ID',

    /* The Klaviyo list that klaviyoSubscribe() adds new subscribers to. Must
       live inside the same account as klaviyoCompanyId above - a list id from a
       different account is accepted by the request and then silently drops the
       subscriber.

       WHERE TO GET IT: Klaviyo -> Audience -> Lists & Segments -> open the list
       -> Settings -> List ID, or read the id out of the list's URL. */
    klaviyoListId: 'REPLACE_ME_KLAVIYO_LIST_ID',

    /* Klaviyo pins its API by date. Bumping this can change response shapes -
       do it deliberately and re-run RegaliaTrack.testKlaviyo() afterward. */
    klaviyoRevision: '2024-10-15',

    /* Free-text label written onto every profile this file creates, as
       custom_source, and used as the subscription source in Klaviyo reporting.
       Purely descriptive - nothing looks up or branches on it.

       WHERE TO GET IT: choose it. Name the store and the capture point, e.g.
       "<Store name> Waitlist Popup", so acquisition reports stay readable when
       several stores report into separate accounts. */
    klaviyoSource: 'REPLACE_ME_KLAVIYO_SOURCE',

    /* Leave EMPTY while the Facebook & Instagram app's pixel is reachable as
       window.fbq (see runtimeContext note in the header). Filling this in makes
       THIS file inject Meta's base code itself - it only does so when
       window.fbq is absent, so it can never double-load and double-count.

       This is the recovery lever if Shopify ever flips that pixel to STRICT:
       paste the pixel id here and Meta reporting keeps working. */
    metaPixelId: '',

    debug: /[?&]rgtrack=1\b/.test(window.location.search)
  };

  var STORE = {
    id: 'regalia:did',
    attr: 'regalia:attr'
  };

  function log() {
    if (!CONFIG.debug || !window.console || !console.log) return;
    try { console.log.apply(console, ['[regalia-track]'].concat([].slice.call(arguments))); } catch (e) {}
  }

  /* ── Small helpers ─────────────────────────────────────────────────────── */

  function uuid() {
    try {
      if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
      if (window.crypto && window.crypto.getRandomValues) {
        var b = new Uint8Array(16);
        window.crypto.getRandomValues(b);
        b[6] = (b[6] & 0x0f) | 0x40;
        b[8] = (b[8] & 0x3f) | 0x80;
        var h = [];
        for (var i = 0; i < 16; i++) h.push((b[i] + 0x100).toString(16).slice(1));
        return h.slice(0, 4).join('') + '-' + h.slice(4, 6).join('') + '-' +
               h.slice(6, 8).join('') + '-' + h.slice(8, 10).join('') + '-' + h.slice(10).join('');
      }
    } catch (e) {}
    return 'r-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
  }

  function lsGet(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
  function lsSet(key, val) {
    try { window.localStorage.setItem(key, val); } catch (e) {}
  }

  function cookie(name) {
    try {
      var m = document.cookie.match('(?:^|; )' + name.replace(/([.*+?^${}()|[\]\\])/g, '\\$1') + '=([^;]*)');
      return m ? decodeURIComponent(m[1]) : '';
    } catch (e) { return ''; }
  }

  function param(sp, key) {
    try { var v = sp.get(key); return v == null ? '' : String(v).slice(0, 300); }
    catch (e) { return ''; }
  }

  /* Drops empty/undefined keys so PostHog and Klaviyo do not fill up with
     nulls. Both treat a MISSING key very differently from a present-but-empty
     one when you segment on it later. Note that false and 0 are preserved. */
  function compact(obj) {
    var out = {};
    for (var k in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, k)) continue;
      var v = obj[k];
      if (v === null || v === undefined || v === '') continue;
      if (typeof v === 'number' && isNaN(v)) continue;
      out[k] = v;
    }
    return out;
  }

  function merge() {
    var out = {};
    for (var a = 0; a < arguments.length; a++) {
      var src = arguments[a];
      if (!src) continue;
      for (var k in src) {
        if (Object.prototype.hasOwnProperty.call(src, k)) out[k] = src[k];
      }
    }
    return out;
  }

  /* ── The stable id ─────────────────────────────────────────────────────── */

  var regaliaId = lsGet(STORE.id);
  if (!regaliaId) {
    regaliaId = uuid();
    lsSet(STORE.id, regaliaId);
  }

  function phDistinctId() {
    try {
      if (window.posthog && typeof window.posthog.get_distinct_id === 'function') {
        var d = window.posthog.get_distinct_id();
        if (d) return String(d);
      }
    } catch (e) {}
    return '';
  }

  function distinctId() {
    return phDistinctId() || regaliaId;
  }

  /* ── Klaviyo's cookie, for the reverse lookup ──────────────────────────── */

  function klaviyoId() {
    var raw = cookie('__kla_id');
    if (!raw) return { raw: '', exchangeId: '', email: '' };
    var out = { raw: raw.slice(0, 500), exchangeId: '', email: '' };
    try {
      var json = JSON.parse(window.atob(raw));
      out.exchangeId = json.$exchange_id || '';
      out.email = json.$email || '';
    } catch (e) {
    }
    return out;
  }

  /* ── Attribution ───────────────────────────────────────────────────────── */

  /* Meta rewrites its ad params onto the landing URL, but the advertorial's
     CTAs point at a bare https://regaliabeauty.com/?from=... - so by the time
     the visitor reaches the PDP the ad params are gone from the query string.
     Both pages are the same origin, so first/last touch is persisted here once
     and replayed as PostHog super properties on every event from either page. */
  var ATTR_KEYS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id',
    'fbclid', 'gclid', 'ttclid',
    'ad_id', 'adset_id', 'campaign_id',
    'ad_name', 'adset_name', 'campaign_name',
    'placement', 'site_source_name'
  ];

  function readUrlAttr() {
    var found = {};
    if (!window.URLSearchParams) return found;
    var sp;
    try { sp = new window.URLSearchParams(window.location.search); } catch (e) { return found; }
    for (var i = 0; i < ATTR_KEYS.length; i++) {
      var v = param(sp, ATTR_KEYS[i]);
      if (v) found[ATTR_KEYS[i]] = v;
    }
    return found;
  }

  function loadAttr() {
    var raw = lsGet(STORE.attr);
    if (!raw) return { first: null, last: null };
    try {
      var parsed = JSON.parse(raw);
      return { first: parsed.first || null, last: parsed.last || null };
    } catch (e) { return { first: null, last: null }; }
  }

  var attribution = (function () {
    var stored = loadAttr();
    var fresh = readUrlAttr();
    var hasFresh = false;
    for (var k in fresh) { if (Object.prototype.hasOwnProperty.call(fresh, k)) { hasFresh = true; break; } }

    if (hasFresh) {
      fresh.landing_page = window.location.pathname;
      fresh.landing_at = new Date().toISOString();
      fresh.referrer = document.referrer || '';
      if (!stored.first) stored.first = fresh;
      stored.last = fresh;
      lsSet(STORE.attr, JSON.stringify(stored));
    }

    var props = {};
    var last = stored.last || {};
    var first = stored.first || {};
    for (var a = 0; a < ATTR_KEYS.length; a++) {
      var key = ATTR_KEYS[a];
      if (last[key]) props[key] = last[key];
      if (first[key]) props['first_' + key] = first[key];
    }
    if (first.landing_page) props.first_landing_page = first.landing_page;
    if (first.referrer) props.first_referrer = first.referrer;
    if (last.landing_page) props.landing_page = last.landing_page;
    return props;
  })();

  /* ── PostHog ───────────────────────────────────────────────────────────── */

  /* PostHog's own loader, copied verbatim from the project's settings page. It
     stubs the SDK surface so calls made before array.js lands are queued rather
     than thrown. Do not hand-edit the mangled method list - it tracks a specific
     posthog-js build; re-copy the whole snippet if it is ever refreshed. */
  !function(t,e){var o,n,p,r;e.__SV||(window.posthog && window.posthog.__loaded)||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}p||((p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",p.onerror=function(){p=null},(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r));var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],Object.defineProperty(u,"toString",{configurable:!0,enumerable:!0,writable:!0,value:function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e}}),Object.defineProperty(u.people,"toString",{configurable:!0,enumerable:!0,writable:!0,value:function(){return u.toString(1)+".people (stub)"}}),o="Gl Zl Jl Ql Yl init wu ku yu bu Tu Pa Iu pu Pu Fu Ou capture getExtension Su Wl Lu calculateEventProperties Du register register_once register_for_session unregister unregister_for_session Bu mu Nu getFeatureFlag getFeatureFlagPayload getFeatureFlagResult getAllFeatureFlags isFeatureEnabled reloadFeatureFlags updateFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSurveysLoaded onSessionId getSurveys getActiveMatchingSurveys renderSurvey displaySurvey cancelPendingSurvey canRenderSurvey canRenderSurveyAsync Hu identify setPersonProperties unsetPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset zu shutdown setIdentity clearIdentity get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException addExceptionStep captureLog startExceptionAutocapture stopExceptionAutocapture loadToolbar get_property getSessionProperty qu Au createPersonProfile setInternalOrTestUser ju tu eu opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing get_explicit_consent_status is_capturing clear_opt_in_out_capturing Eu debug Fa bs getPageViewId captureTraceFeedback captureTraceMetric du".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);

  try {
    if (!(window.posthog && window.posthog.__loaded)) window.posthog.init(CONFIG.posthogToken, {
      api_host: CONFIG.posthogHost,
      ui_host: CONFIG.posthogUiHost,
      defaults: CONFIG.posthogDefaults,
      person_profiles: 'always'
    });
  } catch (e) { log('posthog init failed', e); }

  var kla = klaviyoId();
  try {
    window.posthog.register(compact(merge(attribution, {
      regalia_id: regaliaId,
      klaviyo_id: kla.exchangeId || kla.raw,
      shopify_shop: (window.Shopify && window.Shopify.shop) || ''
    })));
  } catch (e) { log('posthog register failed', e); }

  function capture(name, props, opts) {
    try {
      window.posthog.capture(name, compact(merge({
        regalia_id: regaliaId,
        external_id: distinctId()
      }, props)), opts);
    } catch (e) { log('capture failed', name, e); }
  }

  /* Keeps the distinct_id we already gave Meta and Klaviyo, and hangs the email
     off it as a person property. Re-identifying UNDER the email would mint a new
     distinct_id and break the very join this file exists to create.

     klaviyoId() is re-read here rather than reusing the module-level `kla`
     snapshot: Klaviyo writes its __kla_id cookie AFTER page load, and always
     after a signup - so the load-time snapshot is empty for exactly the session
     in which someone hands over an email, which is the session that matters. */
  function identifyEmail(email, props) {
    if (!email) return;
    var klaNow = klaviyoId();
    try {
      window.posthog.identify(distinctId(), compact(merge({
        email: email,
        regalia_id: regaliaId,
        klaviyo_id: klaNow.exchangeId || klaNow.raw || kla.exchangeId || kla.raw
      }, props)));
    } catch (e) { log('identify failed', e); }
  }

  /* ── Meta pixel ────────────────────────────────────────────────────────── */

  /* Inert while CONFIG.metaPixelId is empty, which it is today. Kept on purpose:
     it is the only way to restore Meta reporting from the theme if the Facebook
     app's pixel is ever sandboxed. Guarded on window.fbq so it can never load a
     second base pixel over a working one and double-count every event. */
  function loadMetaBase(pixelId) {
    if (!pixelId || typeof window.fbq === 'function') return;
    !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
    n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(
      window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    try {
      window.fbq('init', pixelId, { external_id: distinctId() });
      window.fbq('track', 'PageView');
    } catch (e) { log('fbq init failed', e); }
  }
  loadMetaBase(CONFIG.metaPixelId);

  /* True when a call made RIGHT NOW would actually reach fbevents.js in this
     window. Shopify loads the pixel asynchronously, so this is false for the
     first few hundred ms of a page and true afterward - which is why callers
     must ask at call time, never once at script time. */
  function metaReady() {
    return typeof window.fbq === 'function';
  }

  /* Fires a Meta STANDARD event and returns the eventID used, so the caller can
     put the same value on the matching PostHog event and have the pair
     reconciled (and deduplicated against a CAPI copy later).

     Standard events only, deliberately - see the header on why no trackCustom
     counterpart exists here. */
  function fb(eventName, params, eventId) {
    var id = eventId || uuid();
    var payload = compact(merge({ external_id: distinctId() }, params));
    var pixelReached = false;

    try {
      if (metaReady()) {
        window.fbq('track', eventName, payload, { eventID: id });
        pixelReached = true;
      }
    } catch (e) { log('fbq track failed', eventName, e); }

    /* Published even when fbq was reachable, so a Custom Pixel that also relays
       would deduplicate on eventID rather than double-count.

       IMPORTANT: as of now there is NO Custom Pixel in the store subscribing to
       'regalia_meta_event', so this publish currently goes nowhere. It is kept
       as the ready-made hook if the Facebook app's pixel is ever sandboxed.

       It deliberately does NOT count toward pixelReached. It used to, which
       quietly defeated the whole diagnostic below: Shopify.analytics.publish
       almost always succeeds, so the "no pixel reachable" warning could never
       fire even when fbq was genuinely missing and Meta was receiving nothing. */
    try {
      var sa = window.Shopify && window.Shopify.analytics;
      if (sa && typeof sa.publish === 'function') {
        sa.publish('regalia_meta_event', {
          name: eventName,
          method: 'track',
          event_id: id,
          params: payload
        });
      }
    } catch (e) { log('analytics.publish failed', eventName, e); }

    log('meta', eventName, id, payload, pixelReached ? '' : '(NO PIXEL REACHED)');
    if (!pixelReached && window.console && console.warn) {
      console.warn('[regalia-track] window.fbq was not reachable for "' + eventName +
                   '", so Meta received nothing. Most likely the Facebook & Instagram ' +
                   'app pixel flipped to a sandboxed runtimeContext. Fix by setting ' +
                   'metaPixelId in 1-custom-track.js, or by adding a Custom Pixel ' +
                   'that relays the regalia_meta_event published above.');
    }
    return id;
  }

  /* ── Klaviyo (public client endpoints - public key only, CORS-enabled) ─── */

  /* NO PAGE CALLS THESE TODAY. The waitlist popup was the only caller of
     klaviyoSubscribe / klaviyoEvent / identifyEmail, and it was removed when the
     store started taking real orders. They are kept whole, together with
     testKlaviyo() below, because the credentials, the list id and the exact
     payload shapes here were established against the live API and are expensive
     to rediscover - the next email capture should reuse them rather than rebuild
     them. The names below still refer to the real Klaviyo list, which is called
     "Regalia Waitlist"; do not rename them to match the theme. */

  /* Rejected, never thrown: a signup form chains .catch() to re-enable its
     submit button, and a synchronous throw would leave it spinning forever. */
  function klaviyoPost(path, body) {
    if (typeof fetch !== 'function') {
      return Promise.reject(new Error('fetch unavailable'));
    }
    var url = 'https://a.klaviyo.com/client/' + path + '/?company_id=' +
              encodeURIComponent(CONFIG.klaviyoCompanyId);
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: CONFIG.klaviyoRevision
      },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (res.ok) return true;
      return res.text().then(function (raw) {
        /* Klaviyo names the exact offending field. Surfacing detail + pointer
           rather than the raw blob is the difference between a five-minute fix
           and a guessing game - the errors that actually bite here are schema
           errors, not network errors. */
        var detail = '', pointer = '';
        try {
          var first = (JSON.parse(raw).errors || [])[0] || {};
          detail = first.detail || '';
          pointer = (first.source && first.source.pointer) || '';
        } catch (e) {}
        var msg = 'Klaviyo /client/' + path + '/ ' + res.status +
                  (detail ? ' - ' + detail : '') +
                  (pointer ? ' [' + pointer + ']' : '') +
                  (detail ? '' : ' - ' + (raw || '(no body)'));
        if (window.console && console.error) {
          console.error('[regalia-track] ' + msg, { status: res.status, body: raw, sent: body });
        }
        var err = new Error(msg);
        err.klaviyoStatus = res.status;
        err.klaviyoDetail = detail;
        err.klaviyoPointer = pointer;
        throw err;
      });
    });
  }

  /* Records consent and puts them on the Regalia Waitlist.

     DO NOT add a `subscriptions: { email: { marketing: ... } }` block to the
     profile here. That shape belongs to the SERVER-side subscription APIs; this
     client endpoint rejects it outright with
        400 "'subscriptions' is not a valid field for the resource 'profile'"
        source.pointer /data/attributes/profile/data/attributes/subscriptions
     and the entire signup is lost. Consent comes from the list's own opt-in
     setting instead - the Regalia Waitlist is single opt-in, so a 202 here
     subscribes them directly with no confirmation email.

     `properties` IS accepted on this profile; verified against the live API. */
  function klaviyoSubscribe(email, profileProps) {
    return klaviyoPost('subscriptions', {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: CONFIG.klaviyoSource,
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                properties: compact(profileProps)
              }
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: CONFIG.klaviyoListId } }
        }
      }
    });
  }

  function klaviyoEvent(metricName, email, props, value, currency) {
    var attrs = {
      properties: compact(props),
      time: new Date().toISOString(),
      unique_id: uuid(),
      metric: { data: { type: 'metric', attributes: { name: metricName } } },
      profile: {
        data: {
          type: 'profile',
          attributes: {
            email: email,
            properties: compact({
              posthog_distinct_id: distinctId(),
              regalia_id: regaliaId
            })
          }
        }
      }
    };
    if (typeof value === 'number' && !isNaN(value)) {
      attrs.value = value;
      if (currency) attrs.value_currency = currency;
    }
    return klaviyoPost('events', { data: { type: 'event', attributes: attrs } });
  }

  /* ── Public surface ────────────────────────────────────────────────────── */

  window.RegaliaTrack = {
    config: CONFIG,
    regaliaId: regaliaId,
    distinctId: distinctId,
    klaviyoId: klaviyoId,
    attribution: attribution,

    uuid: uuid,
    compact: compact,
    merge: merge,
    cookie: cookie,

    capture: capture,
    identifyEmail: identifyEmail,
    fb: fb,
    metaReady: metaReady,
    klaviyoSubscribe: klaviyoSubscribe,
    klaviyoEvent: klaviyoEvent,

    /* Console diagnostics. Not called by any page - they exist to be pasted
       into the browser console on the live site, and are kept in the shipped
       bundle deliberately so that debugging a live tracking problem never needs
       a theme deploy.

       metaStatus() separates the three failures that look identical from Meta
       Events Manager: no pixel in this window (fbq false), a pixel that never
       saw the ad click (fbc empty on a visit that arrived with an fbclid), and
       everything fine but the event simply not being sent. */
    metaStatus: function () {
      var sa = window.Shopify && window.Shopify.analytics;
      var pixels = [];
      try {
        var state = window.fbq && window.fbq.getState && window.fbq.getState();
        var list = (state && state.pixels) || [];
        for (var i = 0; i < list.length; i++) pixels.push(list[i].id);
      } catch (e) {}
      return {
        fbq: metaReady(),
        pixel_ids: pixels,
        shopify_bus: !!(sa && typeof sa.publish === 'function'),
        fbc: cookie('_fbc'),
        fbp: cookie('_fbp'),
        regalia_id: regaliaId,
        page: window.location.pathname
      };
    },

    /* RegaliaTrack.testKlaviyo('you+test@example.com')
       Hits both Klaviyo endpoints with the real payload shape and prints the
       outcome, so a Klaviyo problem can be told apart from a popup problem in
       one line. WARNING: this creates a REAL profile on the live waitlist -
       use an address you are willing to delete. */
    testKlaviyo: function (email) {
      var out = function (label) {
        return function (r) {
          var ok = r === true;
          /* eslint-disable no-console */
          console.log('%c' + label + ': ' + (ok ? 'OK (202)' : 'FAILED'),
                      'font-weight:700;color:' + (ok ? '#1c7c43' : '#c0261a'),
                      ok ? '' : r);
        };
      };
      klaviyoSubscribe(email, { regalia_id: regaliaId, source: 'testKlaviyo' })
        .then(out('subscriptions')).catch(out('subscriptions'));
      klaviyoEvent('Waitlist Signup', email,
                   { source: 'testKlaviyo', regalia_id: regaliaId }, 1, 'AUD')
        .then(out('events')).catch(out('events'));
      return 'probing Klaviyo for ' + email + ' - watch this console';
    },

    /* Page-level segmentation, called once by each section, so advertorial and
       PDP traffic stay separable inside one PostHog project. */
    setPage: function (name) {
      try { window.posthog.register({ regalia_page: name }); } catch (e) {}
    },

    /* Everything a click handler needs to describe where it was clicked. */
    clickContext: function (el) {
      if (!el) return {};
      var host = el.closest ? el.closest('[data-track-location], section[id], [id]') : null;
      var text = (el.textContent || '').replace(/\s+/g, ' ').trim();
      /* className is an SVGAnimatedString (not a string) on SVG elements, so a
         naive .slice() on it would throw when someone clicks an inline icon. */
      var cls = typeof el.className === 'string' ? el.className : '';
      return compact({
        cta_text: text.slice(0, 120),
        cta_href: (el.getAttribute && el.getAttribute('href')) || '',
        cta_tag: (el.tagName || '').toLowerCase(),
        cta_classes: cls.slice(0, 160),
        cta_location: host ? (host.getAttribute('data-track-location') || host.id || '') : ''
      });
    }
  };

  log('ready', { regalia_id: regaliaId, attribution: attribution, klaviyo: kla });
})(window, document);
